import type { PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';

import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LeftFilters,
  SearchLayoutContainer,
  SearchLayoutMain,
  VerticalScroll,
} from '@sorare/core/src/components/search/SearchLayout/ui';
import type { GallerySortNames } from '@sorare/core/src/contexts/config';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

import { BackendActiveFilters } from '#src/search/ActiveFilters/index.tsx';
import { BackendSavedFilters } from '#src/search/BackendSavedFilters/index.tsx';
import { FiltersManager } from '#src/search/FiltersManager/index.tsx';
import { BackendFiltersManagerDialog } from '#src/search/FiltersManagerDialog/index.tsx';
import { BackendSearchBox } from '#src/search/SearchBox/index.tsx';
import { BackendToggleFiltersManager } from '#src/search/ToggleFiltersManager/index.tsx';
import { BackendSortCards } from '#src/searchCards/BackendSortCards/index.tsx';
import { BackendTotalResults } from '#src/searchCards/BackendTotalResults/index.tsx';
import { BackendSegment } from '#src/searchCards/Segment/BackendSegment.tsx';

const SortAndSegment = styled(Vertical).attrs({ gap: 1.5 })`
  @media ${laptopAndAbove} {
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: flex-start;
  }
`;
const SortCtn = styled(Horizontal)`
  width: 100%;
  padding-bottom: var(--intermediate-unit);
  border-bottom: 1px solid rgba(var(--c-rgb-white), 0.11);
  @media ${laptopAndAbove} {
    width: auto;
    padding-bottom: 0;
    border-bottom: none;
  }
`;
const SearchCtn = styled(Vertical).attrs({ gap: 2 })`
  padding: 0 0 var(--double-unit);
`;

const FiltersManagerWrapper = styled.div`
  margin-bottom: var(--double-unit);
`;

const paddedSortRow = css`
  padding-top: var(--double-unit);
`;

type Props = PropsWithChildren<{
  banner?: ReactNode;
  cardFilters: FiltersGroup[];
  sorts: GallerySortNames;
  searchBoxPlaceholder: string;
  sortRowExtra?: ReactNode;
  showSavedFilters?: boolean;
  sortRowTopPadding?: boolean;
}>;

export const BackendMarketSearchLayout = ({
  banner,
  cardFilters,
  sorts,
  searchBoxPlaceholder,
  sortRowExtra,
  showSavedFilters = false,
  sortRowTopPadding = false,
  children,
}: Props) => {
  const { up: isLaptop } = useScreenSize('laptop');
  const [filtersOpen, toggleFilters] = useToggle(false);

  return (
    <Vertical gap={3}>
      {banner}
      <SearchLayoutContainer>
        {!!isLaptop && (
          <VerticalScroll>
            <LeftFilters>
              <SearchCtn>
                <BackendSearchBox placeholder={searchBoxPlaceholder} />
                {!!showSavedFilters && <BackendSavedFilters />}
                <Horizontal>
                  <BackendTotalResults />
                </Horizontal>
              </SearchCtn>
              <FiltersManager filters={cardFilters} skipClearAllButton />
            </LeftFilters>
          </VerticalScroll>
        )}
        <SearchLayoutMain>
          <SortAndSegment
            className={sortRowTopPadding ? paddedSortRow : undefined}
          >
            <SortCtn>
              {!isLaptop && (
                <BackendSearchBox placeholder={searchBoxPlaceholder} />
              )}
              {!isLaptop && !!showSavedFilters && <BackendSavedFilters />}
              {sortRowExtra}
              {!!isLaptop && <BackendSortCards indexes={sorts} />}
            </SortCtn>
            <BackendSegment />
          </SortAndSegment>
          <BackendActiveFilters />
          {children}
        </SearchLayoutMain>
      </SearchLayoutContainer>
      {!isLaptop && (
        <>
          <Portal id="above-bottom-bar-portal">
            <FiltersManagerWrapper>
              <BackendToggleFiltersManager toggleFilters={toggleFilters} />
            </FiltersManagerWrapper>
          </Portal>
          <BackendFiltersManagerDialog
            open={filtersOpen}
            onClose={toggleFilters}
            filtersGroups={cardFilters}
            sorts={sorts}
          >
            <BackendTotalResults />
          </BackendFiltersManagerDialog>
        </>
      )}
    </Vertical>
  );
};
